"use client";

// src/components/organisms/toast/toast.tsx
import { cn } from "@base/utils/cn";
import { Icon } from "@core/icons";
import {
  Action as RadixToastAction,
  Close as RadixToastClose,
  Description as RadixToastDescription,
  Provider as RadixToastProvider,
  Root as RadixToastRoot,
  Title as RadixToastTitle,
  Viewport as RadixToastViewport
} from "@radix-ui/react-toast";
import { cva } from "class-variance-authority";
import {
  forwardRef
} from "react";
import { jsx } from "react/jsx-runtime";
var ToastViewport = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  RadixToastViewport,
  {
    ref,
    className: cn(
      "fixed top-0 right-0 z-[100] flex max-h-dvh w-full flex-col p-4 md:max-w-[\
420px]",
      className
    ),
    ...props
  }
));
ToastViewport.displayName = "ToastViewport";
var toastVariants = cva(
  "group pointer-events-auto relative flex w-full items-center justify-between s\
pace-x-4 overflow-hidden rounded-md border p-6 pr-8 shadow-lg transition-all dat\
a-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-s\
wipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] dat\
a-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:\
animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[s\
tate=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full",
  {
    variants: {
      variant: {
        default: "border-border bg-background/95 backdrop-blur supports-[backdro\
p-filter]:bg-background/80 text-foreground",
        success: "border-success bg-success/95 backdrop-blur supports-[backdrop-\
filter]:bg-success/80 text-success-foreground",
        warning: "border-warning bg-warning/95 backdrop-blur supports-[backdrop-\
filter]:bg-warning/80 text-warning-foreground",
        destructive: "destructive group border-destructive bg-destructive/95 bac\
kdrop-blur supports-[backdrop-filter]:bg-destructive/80 text-destructive-foregro\
und"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
var Toast = forwardRef(({ className, variant, ...props }, ref) => /* @__PURE__ */ jsx(
RadixToastRoot, { ref, className: cn(toastVariants({ variant }), className), ...props }));
Toast.displayName = RadixToastRoot.displayName;
var ToastAction = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  RadixToastAction,
  {
    ref,
    className: cn(
      "inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg\
-transparent px-3 text-sm font-medium ring-offset-background transition-colors h\
over:bg-secondary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-off\
set-2 disabled:pointer-events-none disabled:opacity-50 group-[.destructive]:bord\
er-muted/40 group-[.destructive]:hover:border-destructive/30 group-[.destructive\
]:hover:bg-destructive group-[.destructive]:hover:text-destructive-foreground gr\
oup-[.destructive]:focus:ring-destructive",
      className
    ),
    ...props
  }
));
ToastAction.displayName = RadixToastAction.displayName;
var ToastClose = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  RadixToastClose,
  {
    ref,
    className: cn(
      "absolute right-2 top-2 rounded-md p-1 text-foreground/50 opacity-0 transi\
tion-opacity hover:text-foreground focus:opacity-100 focus:outline-none focus:ri\
ng-2 group-hover:opacity-100 group-[.destructive]:text-red-3 group-[.destructive\
]:hover:text-red-2 group-[.destructive]:focus:ring-red-4 group-[.destructive]:fo\
cus:ring-offset-red-6",
      className
    ),
    "toast-close": "",
    ...props,
    children: /* @__PURE__ */ jsx(Icon, { type: "lucide", icon: "X", className: "\
size-4" })
  }
));
ToastClose.displayName = RadixToastClose.displayName;
var ToastTitle = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
RadixToastTitle, { ref, className: cn("text-sm font-semibold", className), ...props }));
ToastTitle.displayName = RadixToastTitle.displayName;
var ToastDescription = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
RadixToastDescription, { ref, className: cn("text-sm opacity-90", className), ...props }));
ToastDescription.displayName = RadixToastDescription.displayName;
export {
  Toast,
  ToastAction,
  ToastClose,
  ToastDescription,
  RadixToastProvider as ToastProvider,
  ToastTitle,
  ToastViewport
};
